import "../../components/welcomeScreen/welcomeWizard.scss";

import PropTypes from "prop-types";
import React from "react";

import FormDropdown from "../../components/form/FormDropdown";
import Icon from "../../components/interface/Icon";
import ModalBlock from "../../components/modal/ModalBlock";
import ModalBody from "../../components/modal/ModalBody";
import ModalCheckboxGrid from "../../components/modal/ModalCheckboxGrid";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import ModalTabContent from "../../components/modal/ModalTabContent";
import SyncCustomFieldOptions from "../../constants/SyncCustomFieldOptions";
import syncFieldTypes from "../../constants/syncFieldTypes";
import syncLocalFields from "../../constants/syncLocalFields";
import SyncTypeOptions from "../../constants/SyncTypeOptions";
import { propertySelectors } from "../../modules/definition/properties";
import { typeSelectors } from "../../modules/definition/types";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsOperations, syncSettingsSelectors } from "../../modules/sync/settings";
import selectors from "./selectors";

const instructions = [
  <div key="syncFields" className="welcomeWizardAttributeItem">
    <Icon icon="tick" />
    <div className="welcomeWizardAttributeItemLabel">Select which fields you want to sync for each document</div>
  </div>,
  <div key="syncNotAllFields" className="welcomeWizardAttributeItem">
    <Icon icon="cross" />
    <div className="welcomeWizardAttributeItemLabel">
      Not all fields will be supported by all documents (see Applies to list for each field)
    </div>
  </div>,
  <div key="syncKeywords" className="welcomeWizardAttributeItem">
    <Icon icon="tick" />
    <div className="welcomeWizardAttributeItemLabel">Choose to sync keywords and relationships on the next page</div>
  </div>
];

const onCustomFieldChange = (id, value, dispatchLocal) => {
  if (!value || value === SyncTypeOptions.NONE || value === SyncCustomFieldOptions.NONE) {
    // Selected None or Off, so remove sync field
    dispatchLocal(syncSettingsOperations.removeSyncField(id));
  } else {
    // Otherwise, customFieldId is the value passed in
    dispatchLocal(syncSettingsOperations.configureSyncField(id, value));
  }
};

const typeLabelsWithDates = typesById => {
  return typesById
    .filter(t => t.canHaveDate)
    .map(t => t.pluralLabel)
    .toList()
    .join(", ");
};

const typeLabelsForProperty = (typesById, propertyId) => {
  return typesById
    .filter(t => t.properties.includes(propertyId))
    .map(t => t.pluralLabel)
    .toList()
    .join(", ");
};

const renderOptions = props => {
  const appName = syncSettingsSelectors.getSyncApplicationName(props.localStore);
  const allSyncTypeIds = syncSettingsSelectors.getSelectedSyncTypeSet(props.localStore);

  const properties = propertySelectors.getOrderedPropertiesForTypeIds(props.localStore, allSyncTypeIds);
  const typesById = typeSelectors.getTypesById(props.localStore).filter((t, id) => allSyncTypeIds.has(id));

  const dateTypeLabels = typeLabelsWithDates(typesById);

  const customDropdowns = properties
    .map(p => (
      <ModalRow key={p.label} border={true} alignMiddle={true} alignTop={true}>
        <ModalField flex={1}>
          <span>{p.label}</span>
          <span className="propertySubLabel">Applies to: {typeLabelsForProperty(typesById, p.id)}</span>
        </ModalField>
        <ModalField flex={1}>
          <FormDropdown
            options={selectors.getOptionsForCustomFieldOfType(
              props.localStore,
              syncSelectors.getSyncFieldTypeForProperty(p)
            )}
            value={selectors.getCustomFieldIdForFieldId(props.localStore, p.id)}
            onChange={state => onCustomFieldChange(p.id, state, props.dispatchLocal)}
          />
        </ModalField>
      </ModalRow>
    ))
    .toArray();

  return (
    <ModalTabContent>
      <div className="syncSettingsOptionsPage">
        <ModalGroup label="What will be synced?">
          <ModalRow minFieldWidth={true}>
            <ModalField flex={1}>
              <ModalCheckboxGrid columns={1}>{instructions}</ModalCheckboxGrid>
            </ModalField>
          </ModalRow>
        </ModalGroup>
        <ModalGroup label="Choose Properties">
          <ModalBlock>
            <ModalRow border={true} alignMiddle={true}>
              <ModalField flex={1}>
                <span style={{ opacity: 0.6 }}>Timeline Item Property</span>
              </ModalField>
              <ModalField flex={1}>
                <span style={{ opacity: 0.6 }}>{appName} Field</span>
              </ModalField>
            </ModalRow>
            <ModalRow border={true} alignMiddle={true} alignTop={true}>
              <ModalField flex={1}>
                <span>Label</span>
                <span className="propertySubLabel">Applies to: All Types</span>
              </ModalField>
              <ModalField flex={1}>
                <FormDropdown
                  options={selectors.getOptionsForLabelField(props.localStore)}
                  value={selectors.getCustomFieldIdForFieldId(props.localStore, syncLocalFields.label)}
                  onChange={state => onCustomFieldChange(syncLocalFields.label, state, props.dispatchLocal)}
                />
              </ModalField>
            </ModalRow>
            <ModalRow border={true} alignMiddle={true} alignTop={true}>
              <ModalField flex={1}>
                <span>Summary</span>
                <span className="propertySubLabel">Applies to: All Types</span>
              </ModalField>
              <ModalField flex={1}>
                <FormDropdown
                  options={selectors.getOptionsForSummaryField(props.localStore)}
                  value={selectors.getCustomFieldIdForFieldId(props.localStore, syncLocalFields.summary)}
                  onChange={state => onCustomFieldChange(syncLocalFields.summary, state, props.dispatchLocal)}
                />
              </ModalField>
            </ModalRow>
            <ModalRow border={true} alignMiddle={true} alignTop={true}>
              <ModalField flex={1}>
                <span>Color</span>
                <span className="propertySubLabel">Applies to: All Types</span>
              </ModalField>
              <ModalField flex={1}>
                <FormDropdown
                  options={selectors.getOptionsForCustomFieldOfType(props.localStore, syncFieldTypes.LIST)}
                  value={selectors.getCustomFieldIdForFieldId(props.localStore, syncLocalFields.color)}
                  onChange={state => onCustomFieldChange(syncLocalFields.color, state, props.dispatchLocal)}
                />
              </ModalField>
            </ModalRow>
            <ModalRow key={syncLocalFields.start} border={true} alignMiddle={true} alignTop={true}>
              <ModalField flex={1}>
                <span>Start Date</span>
                <span className="propertySubLabel">Applies to: {dateTypeLabels}</span>
              </ModalField>
              <ModalField flex={1}>
                <FormDropdown
                  options={selectors.getOptionsForDateField(props.localStore)}
                  value={selectors.getCustomFieldIdForFieldId(props.localStore, syncLocalFields.start)}
                  onChange={state => onCustomFieldChange(syncLocalFields.start, state, props.dispatchLocal)}
                />
              </ModalField>
            </ModalRow>
            <ModalRow key={syncLocalFields.end} border={true} alignMiddle={true} alignTop={true}>
              <ModalField flex={1}>
                <span>End Date</span>
                <span className="propertySubLabel">Applies to: {dateTypeLabels}</span>
              </ModalField>
              <ModalField flex={1}>
                <FormDropdown
                  options={selectors.getOptionsForDateField(props.localStore)}
                  value={selectors.getCustomFieldIdForFieldId(props.localStore, syncLocalFields.end)}
                  onChange={state => onCustomFieldChange(syncLocalFields.end, state, props.dispatchLocal)}
                />
              </ModalField>
            </ModalRow>
            {customDropdowns}
          </ModalBlock>
        </ModalGroup>
      </div>
    </ModalTabContent>
  );
};

const SyncPropertyOptionsPage = props => {
  return <ModalBody key="content">{renderOptions(props)}</ModalBody>;
};

SyncPropertyOptionsPage.propTypes = {
  localStore: PropTypes.object.isRequired,
  dispatchLocal: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired
};

renderOptions.propTypes = SyncPropertyOptionsPage.propTypes;

export default SyncPropertyOptionsPage;
